import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";

export function AdminCustomers({ useData }) {
  const { data, loading, reload } = useData("/admin/customers");
  const [preview, setPreview] = useState(null);

  const openDelete = async (u) => {
    try { const { data: rel } = await api.get(`/admin/customers/${u.id}/related`); setPreview(rel); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    const u = preview.user;
    const typed = window.prompt(`${preview.is_test ? "Testkunde" : "ACHTUNG: Dies ist KEIN erkennbarer Testkunde!"}\n\nZum endgültigen Löschen von "${u.name}" inkl. ${preview.requests.length} Anfragen, ${preview.bookings.length} Buchungen und Nachrichten bitte die E-Mail-Adresse exakt eingeben:\n${u.email}`);
    if (typed === null) return;
    try {
      const { data: r } = await api.delete(`/admin/customers/${u.id}`, { params: { confirm: typed } });
      toast.success(`Kunde gelöscht (${Object.values(r).reduce((a, b) => a + b, 0)} Datensätze, Sicherung im Archiv)`); setPreview(null); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div data-testid="admin-customers">
      {preview && (
        <div className="eb-card p-5 mb-5 border border-red-500/40" data-testid="customer-delete-preview">
          <div className="flex items-center gap-2 font-head font-semibold text-red-400"><AlertTriangle className="w-4 h-4" /> Löschvorschau: {preview.user.name} ({preview.user.email}) {preview.is_test ? <span className="eb-badge bg-amber-500/15 text-amber-400">Testkunde</span> : <span className="eb-badge bg-red-500/15 text-red-300">echter Kunde</span>}</div>
          <ul className="text-sm text-slate-300 mt-3 space-y-1">
            <li>{preview.requests.length} Anfragen: {preview.requests.map((r) => r.route_label).join(", ") || "–"}</li>
            <li>{preview.bookings.length} Buchungen: {preview.bookings.map((b) => `${b.route_label} (${b.status}/${b.payment_status})`).join(", ") || "–"}</li>
            <li>{preview.offers} Angebote · {preview.request_messages} Anfrage-Nachrichten · {preview.booking_messages} Buchungs-Nachrichten · {preview.payments} Zahlungsvorgänge</li>
            {preview.paid_bookings > 0 && <li className="text-red-300">Bezahlte Buchungen vorhanden – Löschen gesperrt, bitte zuerst stornieren.</li>}
          </ul>
          <div className="flex gap-2 mt-4">
            <button onClick={doDelete} disabled={preview.paid_bookings > 0} className="eb-btn-primary !bg-red-600 disabled:opacity-40" data-testid="customer-delete-confirm-btn"><Trash2 className="w-4 h-4" /> Endgültig löschen</button>
            <button onClick={() => setPreview(null)} className="eb-btn-outline" data-testid="customer-delete-cancel-btn">Abbrechen</button>
          </div>
        </div>
      )}
      <div className="eb-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left">{["Name", "E-Mail", "Telefon", "Erstellt", ""].map((h) => <th key={h} className="px-4 py-3 eb-label">{h}</th>)}</tr></thead>
          <tbody>
            {data.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Keine Kunden</td></tr> : data.map((u) => (
              <tr key={u.id} className="border-b border-slate-800/50 hover:bg-white/[0.02]" data-testid={`customer-row-${u.id}`}>
                <td className="px-4 py-3 text-slate-200">{u.name}{(u.email?.startsWith("test_") || u.name?.toUpperCase().startsWith("TEST")) && <span className="eb-badge bg-amber-500/15 text-amber-400 ml-2">Test</span>}</td>
                <td className="px-4 py-3"><a href={`mailto:${u.email}`} className="text-[#FF5A1F] underline">{u.email}</a></td>
                <td className="px-4 py-3 text-slate-300">{u.phone || "–"}</td>
                <td className="px-4 py-3 text-slate-300">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => openDelete(u)} className="eb-btn-ghost !px-2 !py-1.5 text-red-400" title="Kunde löschen" data-testid={`customer-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
